import Link from "next/link";
import { BookOpen, Megaphone } from "lucide-react";
import type { FanClubAnnouncementView } from "@/lib/fan-clubs/types";

function formatPublishedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Recently";
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

function AnnouncementLink({
  announcement,
}: {
  announcement: FanClubAnnouncementView;
}) {
  if (!announcement.linkUrl) return null;
  const label =
    announcement.type === "chapter_release" ? "Read chapter" : "View story";

  if (announcement.linkUrl.startsWith("/")) {
    return (
      <Link
        className="font-semibold text-primary hover:underline"
        href={announcement.linkUrl}
      >
        {label}
      </Link>
    );
  }

  return (
    <a
      className="font-semibold text-primary hover:underline"
      href={announcement.linkUrl}
      rel="noopener noreferrer nofollow"
      target="_blank"
    >
      {label}
    </a>
  );
}

export function FanClubAnnouncementList({
  announcements,
  emptyMessage = "No updates yet. The next chapter announcement will appear here.",
}: {
  announcements: FanClubAnnouncementView[];
  emptyMessage?: string;
}) {
  if (announcements.length === 0) {
    return <p className="text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  return (
    <ol className="space-y-3">
      {announcements.map((announcement) => {
        const Icon =
          announcement.type === "chapter_release" ? BookOpen : Megaphone;
        return (
          <li
            key={announcement.id}
            className="rounded-lg border border-border/60 bg-background/40 p-4"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 rounded-full bg-primary/10 p-2 text-primary">
                <Icon className="size-4" aria-hidden="true" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h4 className="font-semibold text-foreground">
                    {announcement.title}
                  </h4>
                  <time
                    className="text-xs text-muted-foreground"
                    dateTime={announcement.publishedAt}
                  >
                    {formatPublishedAt(announcement.publishedAt)}
                  </time>
                </div>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
                  {announcement.body}
                </p>
                <div className="mt-2 text-sm">
                  <AnnouncementLink announcement={announcement} />
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
